import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Lock,
  LockOpen,
  Loader2,
  Plus,
} from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  AdjustmentWithMonths,
  AdjustmentMonth,
} from "@shared-core/data/adapter";
import type { PlanGridBucketRow } from "@shared-core/data/api-types";
import {
  groupPlanBuckets,
  type BucketGroup,
} from "@shared-core/domain/plan-bucket-grouping";
import { Button } from "../../primitives/button";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../primitives/tooltip";
import {
  AdjustmentCell,
  AdjustmentRowEditPopover,
  FY_MONTHS,
  fmtCellDisplay,
  monthKey,
  parseCellInput,
} from "./AdjustmentGrid";
import { fmtK, fmtM } from "@shared-core/domain/format-money";

// Buckets table for the rep page.
//
// One row per bucket header showing its adjusted-forecast per month (read-only
// — driven by the pipeline), with the bucket's adjustments nested directly
// underneath as editable sub-rows. The description column only renders content
// on adjustment sub-rows: bucket header rows put the bucket name there
// instead. Buckets with no adjustments get no sub-rows; "+ Add" opens an
// inline form that creates a new bucket-scope adjustment.
//
// Reuses AdjustmentCell + parseCellInput + the edit popover from
// AdjustmentGrid so the keyboard semantics, formatting, and per-row override
// shape stay aligned with the rep-scope grid below.

// CSS grid template: bucket name (generous, wraps to 2 lines if needed) +
// Fcst FY total + PY FY total + 12 month cells + lock indicator + per-row
// "+ Adjustment" action column.
const GRID_COLS =
  "minmax(220px, 2.5fr) 52px 52px repeat(12, minmax(42px, 1fr)) 28px 32px";

interface Props {
  submissionId: string;
  buckets: PlanGridBucketRow[];
  monthLabels: string[];
  adjustments: AdjustmentWithMonths[];
  onBucketClick: (bucketId: string) => void;
  onAdjustmentsChange: () => void;
}

export function BucketsAdjustmentTable({
  submissionId,
  buckets,
  monthLabels,
  adjustments,
  onBucketClick,
  onAdjustmentsChange,
}: Props) {
  const adapter = useDataAdapter();
  const [savingRowIds, setSavingRowIds] = useState<Set<string>>(new Set());
  const [addingForBucketId, setAddingForBucketId] = useState<string | null>(
    null,
  );
  const gridRef = useRef<HTMLDivElement | null>(null);

  const markSaving = useCallback((id: string, on: boolean) => {
    setSavingRowIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  // Group adjustments by bucket id for O(1) lookup per render.
  const adjsByBucketId = useMemo(() => {
    const map = new Map<string, AdjustmentWithMonths[]>();
    for (const adj of adjustments) {
      if (adj.scopeKind !== "bucket") continue;
      if (adj.forecastSubmissionId !== submissionId) continue;
      const list = map.get(adj.scopeRefId) ?? [];
      list.push(adj);
      map.set(adj.scopeRefId, list);
    }
    return map;
  }, [adjustments, submissionId]);

  // Live per-bucket adjustment-layer per FY month derived from the loaded
  // adjustments — so the bucket header row reflects typed values immediately
  // after each cell save (without waiting for a plan-grid refetch). FY: index
  // 0 = April of 2026, wrapping Jan-Mar into the following calendar year.
  const liveAdjustmentByBucket = useMemo(() => {
    const fyStartYear = 2026;
    const map = new Map<string, number[]>();
    for (const [bucketId, list] of adjsByBucketId) {
      const out = Array(12).fill(0) as number[];
      for (const adj of list) {
        for (const m of adj.months) {
          const idx = m.year === fyStartYear ? m.month - 4 : m.month + 8;
          if (idx >= 0 && idx < 12) out[idx] += m.bookedContributionAmtGbp;
        }
      }
      map.set(bucketId, out);
    }
    return map;
  }, [adjsByBucketId]);

  // Per-adjustment month value map — same shape as in AdjustmentGrid so the
  // cell commits can compute the full months array from the prior state.
  const rowsById = useMemo(() => {
    const map = new Map<string, Map<string, number>>();
    for (const adj of adjustments) {
      if (adj.scopeKind !== "bucket") continue;
      const inner = new Map<string, number>();
      for (const m of adj.months) {
        inner.set(monthKey(m.year, m.month), m.bookedContributionAmtGbp);
      }
      map.set(adj.id, inner);
    }
    return map;
  }, [adjustments]);

  const commitCell = useCallback(
    async (
      adj: AdjustmentWithMonths,
      year: number,
      month: number,
      raw: string,
    ) => {
      const newAmt = parseCellInput(raw);
      const existing = rowsById.get(adj.id);
      const prior = existing?.get(monthKey(year, month)) ?? 0;
      if (newAmt === prior) return;

      const updated: AdjustmentMonth[] = FY_MONTHS.map(
        ({ year: y, month: mo }) => {
          const k = monthKey(y, mo);
          if (y === year && mo === month) {
            return { year: y, month: mo, bookedContributionAmtGbp: newAmt };
          }
          const v = existing?.get(k) ?? 0;
          return { year: y, month: mo, bookedContributionAmtGbp: v };
        },
      ).filter((m) => m.bookedContributionAmtGbp !== 0);

      markSaving(adj.id, true);
      try {
        await adapter.updateAdjustment(adj.id, { months: updated });
        onAdjustmentsChange();
      } finally {
        markSaving(adj.id, false);
      }
    },
    [adapter, markSaving, onAdjustmentsChange, rowsById],
  );

  const handleSaveEdit = useCallback(
    async (
      adj: AdjustmentWithMonths,
      patch: {
        reason: string;
        contributionPct: number | null;
        bookingToInvoiceTimeDays: number | null;
      },
    ) => {
      markSaving(adj.id, true);
      try {
        await adapter.updateAdjustment(adj.id, {
          reason: patch.reason,
          contributionPct: patch.contributionPct,
          bookingToInvoiceTimeDays: patch.bookingToInvoiceTimeDays,
        });
        onAdjustmentsChange();
      } finally {
        markSaving(adj.id, false);
      }
    },
    [adapter, markSaving, onAdjustmentsChange],
  );

  const handleDelete = useCallback(
    async (adj: AdjustmentWithMonths) => {
      markSaving(adj.id, true);
      try {
        await adapter.deleteAdjustment(adj.id);
        onAdjustmentsChange();
      } finally {
        markSaving(adj.id, false);
      }
    },
    [adapter, markSaving, onAdjustmentsChange],
  );

  // Cross-cell keyboard nav: lets users tab through the 12 monthly cells of a
  // row and jump to the next adjustment row's first cell on Enter. Each row
  // gets a unique rowIdx across the whole table so Enter can hop bucket
  // boundaries naturally.
  const onTableKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      if (!target.matches('input[data-cell-input="true"]')) return;
      const rowIdx = parseInt(target.dataset.rowIdx ?? "-1", 10);
      const cellIdx = parseInt(target.dataset.cellIdx ?? "-1", 10);
      if (rowIdx < 0 || cellIdx < 0) return;

      const findInput = (r: number, c: number): HTMLInputElement | null => {
        const root = gridRef.current;
        if (!root) return null;
        return root.querySelector<HTMLInputElement>(
          `input[data-cell-input="true"][data-row-idx="${r}"][data-cell-idx="${c}"]`,
        );
      };

      if (e.key === "Enter") {
        e.preventDefault();
        const next = findInput(rowIdx + 1, 0);
        if (next) {
          next.focus();
          next.select();
        } else {
          target.blur();
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        const original = target.dataset.original ?? "";
        (target as HTMLInputElement).value = original;
        target.blur();
      }
    },
    [],
  );

  // Fold the flat bucket list into tier → account / job-family groups.
  const sections = useMemo(() => groupPlanBuckets(buckets), [buckets]);

  // Expansion overrides per group key; the default is section-driven
  // (Strategic expanded, the rest collapsed) so a rep lands on their key
  // accounts without a wall of open rows.
  const [expandOverrides, setExpandOverrides] = useState<
    Record<string, boolean>
  >({});
  const defaultExpanded = (group: BucketGroup) => group.section === "strategic";
  const isExpanded = useCallback(
    (group: BucketGroup): boolean =>
      expandOverrides[group.key] ?? defaultExpanded(group),
    [expandOverrides],
  );
  const toggleGroup = useCallback((group: BucketGroup) => {
    setExpandOverrides((prev) => ({
      ...prev,
      [group.key]: !(prev[group.key] ?? defaultExpanded(group)),
    }));
  }, []);
  const setAllExpanded = useCallback(
    (open: boolean) => {
      const next: Record<string, boolean> = {};
      for (const s of sections) for (const g of s.groups) next[g.key] = open;
      setExpandOverrides(next);
    },
    [sections],
  );

  // Per-bucket monthly totals = the stacked height the chart draws (baseline +
  // live adjustment + pipeline + actuals). Shared by bucket rows and the
  // group-header roll-up so a collapsed group still shows the real shape.
  const bucketMonthTotals = useCallback(
    (bucket: PlanGridBucketRow): number[] => {
      const live = liveAdjustmentByBucket.get(bucket.forecast_bucket_id);
      return Array.from({ length: 12 }, (_, i) => {
        const base = bucket.baseline_by_month?.[i] ?? 0;
        const liveAdj = live?.[i] ?? bucket.adjustment_by_month?.[i] ?? 0;
        const pipeline = bucket.pipeline_by_month?.[i] ?? 0;
        const actuals = bucket.actuals_by_month?.[i] ?? 0;
        return base + liveAdj + pipeline + actuals;
      });
    },
    [liveAdjustmentByBucket],
  );
  const bucketLiveAdjTotal = useCallback(
    (bucketId: string): number =>
      (liveAdjustmentByBucket.get(bucketId) ?? []).reduce((s, v) => s + v, 0),
    [liveAdjustmentByBucket],
  );

  // The buckets actually in the DOM (members of expanded groups + singletons),
  // in render order — so keyboard-nav row indices bridge boundaries only
  // across visible rows.
  const visibleBuckets = useMemo(() => {
    const out: PlanGridBucketRow[] = [];
    for (const section of sections) {
      for (const group of section.groups) {
        if (group.isSingleton || isExpanded(group)) {
          for (const gb of group.buckets) out.push(gb.bucket);
        }
      }
    }
    return out;
  }, [sections, isExpanded]);
  const rowIdxByAdjId = useMemo(() => {
    const map = new Map<string, number>();
    let idx = 0;
    for (const b of visibleBuckets) {
      const list = adjsByBucketId.get(b.forecast_bucket_id) ?? [];
      for (const adj of list) map.set(adj.id, idx++);
    }
    return map;
  }, [visibleBuckets, adjsByBucketId]);

  // A bucket header row + its adjustment sub-rows + inline add form.
  // ``label`` / ``chips`` come from the grouping (short grain label inside a
  // group, or the account name for a flat singleton); ``indent`` nests the
  // name under its group header; ``emphasize`` shades a flat singleton like a
  // group header (it stands in for one).
  const renderBucketBlock = (
    bucket: PlanGridBucketRow,
    label: string,
    chips: string[],
    indent: boolean,
    emphasize: boolean,
  ) => {
    const bucketAdjs = adjsByBucketId.get(bucket.forecast_bucket_id) ?? [];
    const adjsTotal = bucketLiveAdjTotal(bucket.forecast_bucket_id);
    const isAdding = addingForBucketId === bucket.forecast_bucket_id;
    const monthTotals = bucketMonthTotals(bucket);
    return (
      <div key={bucket.forecast_bucket_id} className="border-b border-border">
        {/* Bucket header row — whole row clickable to drill into bucket. */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => onBucketClick(bucket.forecast_bucket_id)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onBucketClick(bucket.forecast_bucket_id);
            }
          }}
          className={`grid items-center gap-1 px-3 py-2 cursor-pointer transition-colors hover:bg-surface-hover/40 ${
            emphasize ? "bg-surface-hover/40 hover:bg-surface-hover/70" : ""
          }`}
          style={{ gridTemplateColumns: GRID_COLS }}
        >
          <div
            className={`flex items-start gap-2 min-w-0 ${indent ? "pl-6" : ""}`}
            title={label}
          >
            <ChevronRight className="h-3 w-3 shrink-0 mt-0.5 text-primary" />
            <span className="text-primary break-words leading-snug">
              {label}
            </span>
            {chips.map((c) => (
              <Chip key={c}>{c}</Chip>
            ))}
            {adjsTotal !== 0 && (
              <span className="shrink-0 inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 tabular-nums mt-0.5">
                {adjsTotal > 0 ? "+" : ""}
                {fmtK(adjsTotal)}
              </span>
            )}
          </div>
          <div className="text-right text-xs font-semibold tabular-nums">
            {fmtM(bucket.adjusted_gbp)}
          </div>
          <div className="text-right text-xs tabular-nums text-muted-foreground">
            {fmtM(
              (bucket.prior_year_by_month ?? []).reduce((s, v) => s + v, 0),
            )}
          </div>
          {monthTotals.map((cellTotal, i) => (
            <div
              key={i}
              className="text-center text-xs tabular-nums text-muted-foreground overflow-hidden text-ellipsis whitespace-nowrap min-w-0"
            >
              {cellTotal === 0 ? (
                <span className="text-muted-foreground/40">··</span>
              ) : (
                fmtCellDisplay(cellTotal)
              )}
            </div>
          ))}
          <div className="text-center">
            {bucket.is_locked ? (
              <Lock className="h-3.5 w-3.5 inline" />
            ) : (
              <LockOpen className="h-3.5 w-3.5 inline text-muted-foreground/60" />
            )}
          </div>
          <div className="text-center">
            {!isAdding && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAddingForBucketId(bucket.forecast_bucket_id);
                    }}
                    aria-label="Add adjustment to this bucket"
                    className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-primary/10 hover:text-primary"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Add adjustment to this bucket</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Adjustment sub-rows — one per bucket-scope adjustment. */}
        {bucketAdjs.map((adj) => {
          const saving = savingRowIds.has(adj.id);
          const rowIdx = rowIdxByAdjId.get(adj.id) ?? 0;
          const rowTotal = adj.months.reduce(
            (s, m) => s + m.bookedContributionAmtGbp,
            0,
          );
          return (
            <div
              key={adj.id}
              className="grid items-center gap-1 px-3 py-1 hover:bg-surface-hover/20"
              style={{ gridTemplateColumns: GRID_COLS }}
            >
              <div
                className={`flex items-center gap-2 min-w-0 ${indent ? "pl-11" : "pl-5"}`}
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="truncate text-xs" aria-label={adj.reason}>
                      ↳ {adj.reason || "(untitled)"}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="text-xs">
                      <div className="font-medium">
                        {adj.reason || "(untitled)"}
                      </div>
                      {adj.contributionPct !== null && (
                        <div className="text-muted-foreground">
                          margin {adj.contributionPct.toFixed(1)}%
                        </div>
                      )}
                      {adj.bookingToInvoiceTimeDays !== null && (
                        <div className="text-muted-foreground">
                          lead {adj.bookingToInvoiceTimeDays}d
                        </div>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
                {saving && (
                  <Loader2 className="h-3 w-3 animate-spin text-muted-foreground shrink-0" />
                )}
                <div className="ml-auto shrink-0">
                  <AdjustmentRowEditPopover
                    adj={adj}
                    onSave={(patch) => handleSaveEdit(adj, patch)}
                    onDelete={() => handleDelete(adj)}
                  />
                </div>
              </div>
              {/* Sub-row's Fcst column shows this adjustment's row total. */}
              <div className="text-right text-xs tabular-nums">
                {rowTotal === 0 ? (
                  <span className="text-muted-foreground/40">··</span>
                ) : (
                  <span
                    className={
                      rowTotal < 0 ? "text-amber-700" : "text-amber-800"
                    }
                  >
                    {rowTotal > 0 ? "+" : ""}
                    {fmtK(rowTotal)}
                  </span>
                )}
              </div>
              {/* PY column blank for adjustments — PY is a bucket-level total. */}
              <div />
              {FY_MONTHS.map((m, i) => {
                const k = monthKey(m.year, m.month);
                const value = rowsById.get(adj.id)?.get(k) ?? 0;
                return (
                  <AdjustmentCell
                    key={k}
                    rowIdx={rowIdx}
                    cellIdx={i}
                    monthLabel={monthLabels[i] ?? ""}
                    description={adj.reason || "(untitled)"}
                    value={value}
                    onCommit={(raw) => commitCell(adj, m.year, m.month, raw)}
                  />
                );
              })}
              {/* Lock + action columns blank on adjustment sub-rows. */}
              <div />
              <div />
            </div>
          );
        })}

        {/* Inline new-adjustment form */}
        {isAdding && (
          <NewAdjustmentInlineForm
            submissionId={submissionId}
            bucketId={bucket.forecast_bucket_id}
            onCreated={() => {
              setAddingForBucketId(null);
              onAdjustmentsChange();
            }}
            onCancel={() => setAddingForBucketId(null)}
          />
        )}
      </div>
    );
  };

  // A collapsible account / job-family group header carrying the roll-up
  // totals, so a collapsed group is still informative.
  const renderGroupHeader = (group: BucketGroup) => {
    const expanded = isExpanded(group);
    const monthTotals = Array(12).fill(0) as number[];
    let py = 0;
    let liveAdj = 0;
    for (const gb of group.buckets) {
      const mt = bucketMonthTotals(gb.bucket);
      for (let i = 0; i < 12; i++) monthTotals[i] += mt[i];
      py += (gb.bucket.prior_year_by_month ?? []).reduce((s, v) => s + v, 0);
      liveAdj += bucketLiveAdjTotal(gb.bucket.forecast_bucket_id);
    }
    return (
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => toggleGroup(group)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleGroup(group);
          }
        }}
        className="grid items-center gap-1 px-3 py-2 bg-surface-hover/40 cursor-pointer hover:bg-surface-hover/70 transition-colors border-b border-border"
        style={{ gridTemplateColumns: GRID_COLS }}
      >
        <div className="flex items-center gap-2 min-w-0">
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          )}
          <span className="font-medium break-words leading-snug">
            {group.label}
          </span>
          <span className="shrink-0 text-[10px] text-muted-foreground">
            {group.totalCount} buckets
          </span>
          {liveAdj !== 0 && (
            <span className="shrink-0 inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 tabular-nums">
              {liveAdj > 0 ? "+" : ""}
              {fmtK(liveAdj)}
            </span>
          )}
        </div>
        <div className="text-right text-xs font-semibold tabular-nums">
          {fmtM(group.adjustedGbp)}
        </div>
        <div className="text-right text-xs tabular-nums text-muted-foreground">
          {fmtM(py)}
        </div>
        {monthTotals.map((v, i) => (
          <div
            key={i}
            className="text-center text-xs tabular-nums text-muted-foreground overflow-hidden text-ellipsis whitespace-nowrap min-w-0"
          >
            {v === 0 ? (
              <span className="text-muted-foreground/40">··</span>
            ) : (
              fmtCellDisplay(v)
            )}
          </div>
        ))}
        <div className="text-center text-[11px] tabular-nums text-muted-foreground">
          {group.lockedCount}/{group.totalCount}
        </div>
        <div />
      </div>
    );
  };

  return (
    <div className="text-sm" ref={gridRef} onKeyDown={onTableKeyDown}>
      {/* Expand / collapse controls */}
      <div className="flex items-center justify-end gap-2 px-3 py-1 text-xs border-b border-border">
        <button
          type="button"
          onClick={() => setAllExpanded(true)}
          className="text-muted-foreground hover:text-primary hover:underline"
        >
          Expand all
        </button>
        <span className="text-muted-foreground/40">·</span>
        <button
          type="button"
          onClick={() => setAllExpanded(false)}
          className="text-muted-foreground hover:text-primary hover:underline"
        >
          Collapse all
        </button>
      </div>

      {/* Month header row */}
      <div
        className="grid items-center gap-1 px-3 py-1 text-[10px] uppercase tracking-wide text-muted-foreground bg-surface-hover border-b border-border"
        style={{ gridTemplateColumns: GRID_COLS }}
      >
        <div>Account / bucket</div>
        <div className="text-right">Fcst</div>
        <div className="text-right">PY</div>
        {monthLabels.map((m, i) => (
          <div key={i} className="text-center">
            {m}
          </div>
        ))}
        <div className="text-center">Lock</div>
        <div />
      </div>

      {sections.map((section) => (
        <div key={section.section}>
          {/* Tier section header */}
          <div className="px-3 py-1.5 bg-surface-hover/60 border-b border-border text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {section.label} · {section.groups.length}
          </div>
          {section.groups.map((group) =>
            group.isSingleton ? (
              renderBucketBlock(
                group.buckets[0].bucket,
                group.label,
                group.buckets[0].chips,
                false,
                true,
              )
            ) : (
              <div key={group.key}>
                {renderGroupHeader(group)}
                {isExpanded(group) &&
                  group.buckets.map((gb) =>
                    renderBucketBlock(
                      gb.bucket,
                      gb.label,
                      gb.chips,
                      true,
                      false,
                    ),
                  )}
              </div>
            ),
          )}
        </div>
      ))}
    </div>
  );
}

// Small qualifier pill (recurring / work-mode) shown next to a bucket label.
function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="shrink-0 inline-flex items-center rounded-full bg-surface-hover px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
      {children}
    </span>
  );
}

// ── Inline new-row form ──────────────────────────────────────────────

interface NewAdjustmentInlineFormProps {
  submissionId: string;
  bucketId: string;
  onCreated: () => void;
  onCancel: () => void;
}

function NewAdjustmentInlineForm({
  submissionId,
  bucketId,
  onCreated,
  onCancel,
}: NewAdjustmentInlineFormProps) {
  const adapter = useDataAdapter();
  const [description, setDescription] = useState("");
  const [contrib, setContrib] = useState("");
  const [lead, setLead] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Fetch the bucket's calibrated defaults so the placeholders show real
  // numbers ("22.0 (inherit)") instead of a vague "inherit" — gives the
  // manager a sense of what they're overriding before they type.
  const [defaults, setDefaults] = useState<{
    contributionPct?: number;
    bookingToInvoiceDays?: number;
  } | null>(null);
  useEffect(() => {
    let cancelled = false;
    adapter
      .getBucketCalibrationDetail(bucketId)
      .then((d) => {
        if (cancelled) return;
        setDefaults({
          contributionPct: d.contributionPct,
          bookingToInvoiceDays: d.bookingToInvoiceMedianDays,
        });
      })
      .catch(() => {
        // Bucket may not have a calibration record yet; show plain
        // "inherit" rather than failing loudly — it's a hint, not data.
        if (!cancelled) setDefaults({});
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, bucketId]);

  const handleAdd = useCallback(async () => {
    const trimmed = description.trim();
    if (!trimmed) return;
    const contribNum = contrib.trim() === "" ? null : parseFloat(contrib);
    const leadNum = lead.trim() === "" ? null : parseInt(lead, 10);
    setCreating(true);
    setError(null);
    try {
      await adapter.createAdjustment(submissionId, {
        scopeKind: "bucket",
        scopeRefId: bucketId,
        reason: trimmed,
        contributionPct:
          contribNum !== null && Number.isFinite(contribNum)
            ? contribNum
            : null,
        bookingToInvoiceTimeDays:
          leadNum !== null && Number.isFinite(leadNum) ? leadNum : null,
        months: [],
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setCreating(false);
    }
  }, [adapter, bucketId, contrib, description, lead, onCreated, submissionId]);

  return (
    <div className="space-y-2 rounded-none border-l-2 border-dashed border-border bg-surface-hover/20 p-2 pl-5 ml-3">
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[200px] space-y-1">
          <Label className="text-xs text-muted-foreground">
            Description (required)
          </Label>
          <Input
            autoFocus
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAdd();
              } else if (e.key === "Escape") {
                e.preventDefault();
                onCancel();
              }
            }}
            placeholder="e.g. Q4 catalogue push"
            className="h-8"
          />
        </div>
        <div className="w-32 space-y-1">
          <Label className="text-xs text-muted-foreground">
            Contribution %
          </Label>
          <Input
            type="number"
            value={contrib}
            onChange={(e) => setContrib(e.target.value)}
            placeholder={
              defaults?.contributionPct !== undefined
                ? `${defaults.contributionPct.toFixed(1)} (inherit)`
                : "inherit"
            }
            className="h-8"
          />
        </div>
        <div className="w-32 space-y-1">
          <Label className="text-xs text-muted-foreground">Lead days</Label>
          <Input
            type="number"
            value={lead}
            onChange={(e) => setLead(e.target.value)}
            placeholder={
              defaults?.bookingToInvoiceDays !== undefined
                ? `${defaults.bookingToInvoiceDays} (inherit)`
                : "inherit"
            }
            className="h-8"
          />
        </div>
        <Button
          size="sm"
          onClick={handleAdd}
          disabled={creating || !description.trim()}
        >
          {creating ? "Adding…" : "Add"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {error && (
        <p className="text-xs text-red-600 px-1" role="alert">
          Couldn't add adjustment: {error}
        </p>
      )}
    </div>
  );
}
